import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, Loader2, AlertCircle } from "lucide-react";
import SelectDropdown from "../common/SelectDropdown";
import Button from "../common/Button";
import RouteLegend from "./RouteLegend";
import MapView from "./MapView";
import { INDIAN_STATES } from "@/utils/constants";
import { useMap as useMapHook } from "@/hooks/useMap";

const MapSection = () => {
  const {
    selectedState,
    mapCenter,
    mapZoom,
    filteredRoutes,
    isLoading,
    error,
    handleStateChange,
  } = useMapHook();

  const [focusedMapCenter, setFocusedMapCenter] = React.useState(mapCenter);
  const [focusedMapZoom, setFocusedMapZoom] = React.useState(mapZoom);

  // Update focused map when mapCenter/mapZoom changes
  React.useEffect(() => {
    setFocusedMapCenter(mapCenter);
    setFocusedMapZoom(mapZoom);
  }, [mapCenter, mapZoom]);

  const stateName = INDIAN_STATES.find((s) => s.code === selectedState)?.name;

  // Function to calculate center of route from coordinates
  const calculateRouteCenter = (coordinates) => {
    if (!coordinates || coordinates.length === 0) return null;

    // Calculate average lat/lng
    const sum = coordinates.reduce(
      (acc, [lng, lat]) => {
        acc.lat += lat;
        acc.lng += lng;
        return acc;
      },
      { lat: 0, lng: 0 }
    );

    return [sum.lat / coordinates.length, sum.lng / coordinates.length];
  };

  // Handle route click to focus map
  const handleRouteFocus = (route) => {
    const center = calculateRouteCenter(route.coordinates);
    if (center) {
      setFocusedMapCenter(center);
      setFocusedMapZoom(12); // Zoom in to show route details
    }
  };

  // Function to open map in new tab
  const openMapInNewTab = () => {
    const mapUrl = `/map${selectedState ? `?state=${selectedState}` : ""}`;
    const newWindow = window.open(mapUrl, "_blank", "noopener,noreferrer");
    if (newWindow) {
      newWindow.focus();
    }
  };

  const dropdownOptions = INDIAN_STATES.map((s) => ({
    value: s.code,
    label: s.name,
  }));

  return (
    <section className="py-16 bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-800 dark:text-white mb-4">
            Discover Cycling Routes Across India
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Select a state and hover any route to see start, end, and distance
          </p>
        </motion.div>

        <div className="mb-6 flex flex-col md:flex-row gap-4 items-center justify-between z-20 relative">
          <SelectDropdown
            label="Select a State"
            options={dropdownOptions}
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
            placeholder="Choose a state..."
            className="w-full md:w-64"
          />

          <Button
            variant="primary"
            onClick={openMapInNewTab}
            className="bg-green-600 hover:bg-green-700 w-full md:w-auto flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            <span>Open Fullscreen Map</span>
          </Button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Loading routes...</span>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Routes Content */}
        {!isLoading && !error && (
          <>
            <RouteLegend
              routes={filteredRoutes}
              stateName={stateName}
              onRouteClick={handleRouteFocus}
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <MapView
                center={focusedMapCenter}
                zoom={focusedMapZoom}
                routes={filteredRoutes}
              />
            </motion.div>
          </>
        )}

        {/* Empty State */}
        {!isLoading && !error && filteredRoutes.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">
              No routes found {selectedState ? `in ${stateName}` : ""}. Check
              back later!
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default MapSection;
